import {
  AppNavigationLayout,
  useAppLayoutModal,
  useNavigationType,
} from "shared-ts-ui-app-layout";
import { ActionGroup, RouteItem } from "shared-ts-ui-action";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LocalLibraryRoundedIcon from "@mui/icons-material/LocalLibraryRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import FeedbackRoundedIcon from "@mui/icons-material/FeedbackRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import { SizeClass, useWindowWidthComparer } from "shared-ts-ui-responsive";
import React, { useCallback, useEffect } from "react";
import { Typography, useTheme } from "@mui/material";
import { NotificationContextProvider } from "shared-ts-ui-notification";
import RoofingRoundedIcon from "@mui/icons-material/RoofingRounded";
import {
  BooleanSetting,
  NullableDateTimeSetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { useTranslation } from "react-i18next";
import { useUser } from "shared-ts-utils-authentication";
import { McUser } from "firefly-my-chimney-ts-user";
import { Component } from "shared-ts-ui-navigation";
import { PostHogTrackTanstackReactRouterPageView } from "shared-ts-posthog";
import { MyChimneyIcon } from "firefly-my-chimney-ts-shared";
import { useChangeThemeAction } from "shared-ts-ui-app-theme-provider";
import { useNavigation } from "shared-ts-tanstack-react-router-navigation";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { SentryTanstackRouterIntegration } from "shared-ts-sentry";
import { IntroductionPanel } from "my-chimney-ts-introduction";
import { DateTime } from "luxon";

export const RootComponent = () => {
  const theme = useTheme();
  const { t } = useTranslation("my-chimney-ts-translations");
  const navigate = useNavigate();
  const user = useUser<McUser>();
  const { setModal, closeModal } = useAppLayoutModal();
  const [showIntroduction, setShowWelcome] = useUserSettingWithDefault(
    "showIntroduction",
    BooleanSetting,
    true
  );
  const [previousSummaryShownAt, setPreviousSummaryShownAt] =
    useUserSettingWithDefault(
      "previousSummaryShownAt",
      NullableDateTimeSetting,
      null
    );
  const windowWidthComparer = useWindowWidthComparer();
  const appThemePageEnabled = localStorage.getItem("AppThemePage") === "true";
  const changeThemeAction = useChangeThemeAction();
  const { navigationType } = useNavigationType();
  const { navigationItems, handleSelected } = useNavigation(
    [
      {
        type: "Component",
        render: (navigationType) => (
          <MyChimneyIcon
            mode={theme.palette.mode}
            hideHair={false}
            style={{ minHeight: 92, maxHeight: 200 }}
          />
        ),
      } as Component,
      {
        type: "Component",
        render: (navigationType) => (
          <Typography
            variant={"button"}
            align={"center"}
            color={theme.palette.warning.main}
            sx={{
              textTransform: "unset",
              pb: 1,
              fontSize: navigationType === "rail" ? "10px" : undefined,
              fontWeight: navigationType === "rail" ? 500 : undefined,
            }}
          >
            {t("Public Preview")}
          </Typography>
        ),
      } as Component,
      {
        type: "RouteItem",
        selectable: true,
        name: t("Overview"),
        route: "/",
        icon:
          windowWidthComparer.sizeClass === SizeClass.Compact ? (
            <MyChimneyIcon
              hideHair
              hideText
              style={{ maxHeight: 24 }}
              mode={theme.palette.mode}
            />
          ) : (
            <RoofingRoundedIcon />
          ),
        selected: false,
      } as RouteItem,
      {
        type: "RouteItem",
        selectable: true,
        name: t("Burns"),
        route: "/burns",
        icon: <LocalFireDepartmentRoundedIcon />,
        selected: false,
      } as RouteItem,
      {
        type: "RouteItem",
        selectable: true,
        route: "/guidance",
        name: t("Guide"),
        icon: <LocalLibraryRoundedIcon />,
        selected: false,
      } as RouteItem,
      {
        type: "RouteItem",
        selectable: true,
        route: "/history",
        name: t("History"),
        icon: <AnalyticsRoundedIcon />,
        selected: false,
      } as RouteItem,
      {
        type: "RouteItem",
        selectable: true,
        route: "/combustion-plant",
        name: t("Combustion Plant"),
        nameBreakable: t("Combustion Plant:breakable"),
        icon: <SettingsRoundedIcon />,
        selected: false,
      } as RouteItem,
      { type: "Spacer" },
      localStorage.getItem("themePage.enabled") === "true"
        ? ({
            type: "RouteItem",
            selectable: true,
            name: "Theme",
            route: "/theme",
            reference: "/theme",
            icon: <PaletteRoundedIcon />,
          } as RouteItem)
        : undefined,
      {
        type: "ActionGroup",
        icon: <MenuRoundedIcon />,
        preferences: {
          displayInline: true,
        },
        items: [
          {
            type: "RouteItem",
            selectable: true,
            route: "/user",
            name:
              windowWidthComparer.sizeClass === SizeClass.Compact
                ? user.firstName
                : user.fullName,
            icon: <PersonRoundedIcon />,
            selected: false,
          } as RouteItem,
          changeThemeAction,
          appThemePageEnabled
            ? ({
                type: "RouteItem",
                selectable: true,
                route: "/app-theme",
                name: "Theme",
                icon: <PaletteRoundedIcon />,
                selected: false,
              } as RouteItem)
            : undefined,
          {
            type: "RouteItem",
            selectable: true,
            route: "/user-feedback",
            name: t("Feedback"),
            icon: <FeedbackRoundedIcon />,
            selected: false,
          } as RouteItem,
          {
            type: "RouteItem",
            selectable: true,
            route: "/about",
            name: `${t("About")} ${t("My Chimney")}`,
            icon: <InfoRoundedIcon />,
            selected: false,
          } as RouteItem,
        ],
      } as ActionGroup,
    ],
    [windowWidthComparer, t, theme.palette.mode],
    navigationType
  );

  const handleFocus = useCallback(() => {
    const now = DateTime.local();

    if (previousSummaryShownAt != null) {
      const diff = now.diff(previousSummaryShownAt).as("days");
      if (diff >= 1) {
        setPreviousSummaryShownAt(now);
        navigate({
          to: "/summary",
          search: {
            year: now.year,
            month: now.month,
          },
        });
      }
    } else {
      setPreviousSummaryShownAt(now);
    }
  }, [previousSummaryShownAt, navigate, setPreviousSummaryShownAt]);

  /*useEffect(() => {
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [handleFocus]);*/

  useEffect(() => {
    if (showIntroduction) {
      setModal(<IntroductionPanel onClose={() => setShowWelcome(false)} />);
    } else {
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIntroduction]);

  return (
    <SentryTanstackRouterIntegration>
      <NotificationContextProvider>
        <AppNavigationLayout
          variant={"flat"}
          forceBackgroundForAllVariants={true}
          navigationItems={navigationItems}
          main={<Outlet />}
          onNavigationItemSelected={handleSelected}
        />
        <PostHogTrackTanstackReactRouterPageView />
        {/*<TanStackRouterDevtools />*/}
      </NotificationContextProvider>
    </SentryTanstackRouterIntegration>
  );
};
